'use client';

import React, { useEffect, useState, useCallback } from 'react';
import { agendasService, CalendarioAgendasResponse, CitaItem, AsesorAgenda } from '@/lib/api/agendasService';
import { getCurrentUser } from '@/services/authService';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import ErrorMessage from '@/components/common/ErrorMessage';

/**
 * Módulo Agendas:
 * - Vista de calendario por asesores
 * - Muestra asesores expandibles con sus días y horas
 * - Permite editar citas por hora
 * - Filtrado por rango de fechas
 * - Similar al legacy: vista de calendario
 */
const AgendasManagement: React.FC = () => {
  const [calendario, setCalendario] = useState<CalendarioAgendasResponse | null>(null);
  const [expandedAsesores, setExpandedAsesores] = useState<Set<string>>(new Set());
  const [expandedDias, setExpandedDias] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Fechas del rango (por defecto fecha actual)
  const hoy = new Date().toISOString().split('T')[0];
  const [fechaInicio, setFechaInicio] = useState(hoy);
  const [fechaFin, setFechaFin] = useState(hoy);

  const fetchCalendario = useCallback(async () => {
    setLoading(true);
    setError(null);
    const currentUser = getCurrentUser();
    if (!currentUser || !currentUser.ownerID) {
      setError("No se pudo obtener el ID del usuario actual.");
    setLoading(false);
      return;
    }
    
    // Validar fechas mínimas (como en legacy: 2019-01-02)
    const fechaMinima = '2019-01-02';
    const fechaInicioValida = fechaInicio < fechaMinima ? fechaMinima : fechaInicio;
    const fechaFinValida = fechaFin < fechaMinima ? fechaMinima : fechaFin;
    
    try {
      const response = await agendasService.getCalendario(
        currentUser.ownerID,
        fechaInicioValida,
        fechaFinValida
      );
      if (response.data) {
        const data = response.data;

        // Asegurar que cada cita tenga una bandera "guardado" coherente.
        // - Si el backend ya envía guardado (boolean), se respeta.
        // - Si no, se deriva de si la cita tiene datos cargados (actividad, empresa, etc.),
        //   lo cual aplica para eventos existentes ya guardados en BD.
        const citasConGuardado = Object.fromEntries(
          Object.entries(data.citas).map(([asesorId, porFecha]) => [
            asesorId,
            Object.fromEntries(
              Object.entries(porFecha).map(([fecha, porHora]) => [
                fecha,
                Object.fromEntries(
                  Object.entries(porHora).map(([hora, cita]) => {
                    const tieneDatos =
                      !!cita.actividad ||
                      !!cita.empresa ||
                      !!cita.ubicacion ||
                      !!cita.clienteNombre ||
                      !!cita.comentarios;

                    return [
                      hora,
                      {
                        ...cita,
                        guardado:
                          typeof cita.guardado === 'boolean'
                            ? cita.guardado
                            : tieneDatos,
                      },
                    ];
                  })
                ),
              ])
            ),
          ])
        );

        setCalendario({
          ...data,
          citas: citasConGuardado,
        });
        // Expandir todos los asesores por defecto
        const asesoresIds = new Set(response.data.asesores.map(a => a.ownerID));
        setExpandedAsesores(asesoresIds);
      } else {
        setError(response.error || "Error al cargar calendario de agendas.");
      }
    } catch (err: any) {
      setError(err.message || "Error de red al cargar calendario.");
    } finally {
      setLoading(false);
    }
  }, [fechaInicio, fechaFin]);

  useEffect(() => {
    fetchCalendario();
  }, []);

  const toggleAsesor = (ownerID: string) => {
    setExpandedAsesores(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ownerID)) {
        newSet.delete(ownerID);
      } else {
        newSet.add(ownerID);
      }
      return newSet;
    });
  };

  const toggleDia = (diaKey: string) => {
    setExpandedDias(prev => {
      const newSet = new Set(prev);
      if (newSet.has(diaKey)) {
        newSet.delete(diaKey);
      } else {
        newSet.add(diaKey);
      }
      return newSet;
    });
  };

  const handleGuardarCita = async (cita: CitaItem, asesor: AsesorAgenda) => {
    try {
      const currentUser = getCurrentUser();
      if (!currentUser) return;
      
      // Convertir fecha a formato ISO si es necesario
      let fechaISO = cita.fecha;
      if (fechaISO && !fechaISO.includes('T')) {
        fechaISO = `${fechaISO}T00:00:00`;
      }
      
      const payload = {
        ownerID: cita.ownerID,
        userName: asesor.userName,
        fecha: fechaISO,
        hora: cita.hora,
        actividad: cita.actividad || '',
        empresa: cita.empresa || '',
        ubicacion: cita.ubicacion || '',
        clienteNombre: cita.clienteNombre || '',
        clienteNuevoRenovacion: cita.clienteNuevoRenovacion || '',
        comentarios: cita.comentarios || '',
      };
      
      const response = await agendasService.guardar(payload);
      if (response.data) {
        // Recargar calendario para actualizar
        await fetchCalendario();
    } else {
        setError(response.error || "No se pudo guardar la cita.");
      }
    } catch (err: any) {
      setError(err.message || "Error al guardar cita.");
    }
  };

  const handleCitaChange = (asesorID: string, fecha: string, hora: string, field: string, value: string) => {
    if (!calendario) return;
    
    const nuevaCita = { ...calendario.citas[asesorID][fecha][hora] };
    (nuevaCita as any)[field] = value;
    
    setCalendario({
      ...calendario,
      citas: {
        ...calendario.citas,
        [asesorID]: {
          ...calendario.citas[asesorID],
          [fecha]: {
            ...calendario.citas[asesorID][fecha],
            [hora]: nuevaCita
          }
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agendas</h1>
          <p className="text-sm text-gray-500 mt-1">Calendario de citas y actividades por asesor.</p>
        </div>
        <Button variant="secondary" onClick={fetchCalendario}>Consultar</Button>
      </div>

      {error && <ErrorMessage message={error} />}

      {/* Filtros de fecha */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <Input 
            label="Desde" 
            type="date" 
            value={fechaInicio} 
            onChange={(e) => setFechaInicio(e.target.value)}
            min="2019-01-02"
          />
          <Input 
            label="Hasta" 
            type="date" 
            value={fechaFin} 
            onChange={(e) => setFechaFin(e.target.value)}
            min="2019-01-02"
          />
          <Button onClick={fetchCalendario}>Aplicar Filtro</Button>
        </div>
      </div>

      {/* Lista de asesores con calendario */}
      {!calendario ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
          <p className="text-gray-500">No hay datos disponibles.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {calendario.asesores.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
              <p className="text-gray-500">No hay asesores disponibles.</p>
            </div>
          ) : (
            calendario.asesores.map((asesor) => {
              const asesorCitas = calendario.citas[asesor.ownerID] || {};
              const dias = Object.keys(asesorCitas).sort();
              const empresasAsignadas = calendario.empresasPorAsesor[asesor.ownerID] || [];
              
              return (
                <div
                  key={asesor.ownerID}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
                >
                  {/* Header del asesor */}
                  <div
                    className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleAsesor(asesor.ownerID)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{asesor.name}</h3>
                        <p className="text-sm text-gray-500">
                          {asesor.userName} · {asesor.puesto || ''} · {asesor.region || ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">
                          {dias.length} día{dias.length !== 1 ? 's' : ''}
                        </span>
                        <svg
                          className={`w-5 h-5 text-gray-400 transition-transform ${
                            expandedAsesores.has(asesor.ownerID) ? 'transform rotate-180' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Días del calendario */}
                  {expandedAsesores.has(asesor.ownerID) && (
                    <div className="border-t border-gray-100 p-4 bg-gray-50">
                      {dias.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">
                          No hay días disponibles en el rango seleccionado.
                        </p>
                      ) : (
                        <div className="space-y-4">
                          {dias.map((fecha) => {
                            const diaKey = `${asesor.ownerID}_${fecha}`;
                            const horas = Object.keys(asesorCitas[fecha]).sort();
                            const diaLabel = calendario.diasAgenda[fecha] || fecha;

                            // Calcular horas con eventos guardados
                            const horasConEventos = horas.filter((h) => {
                              const c = asesorCitas[fecha][h];
                              return c.guardado === true;
                            });
                            const tieneEventos = horasConEventos.length > 0;

                            return (
                              <div key={fecha} className={`bg-white rounded-lg border overflow-hidden ${tieneEventos ? 'border-blue-300 ring-2 ring-blue-100' : 'border-gray-200'}`}>
                                {/* Header del día */}
                                <div
                                  className={`p-3 cursor-pointer transition-colors ${tieneEventos ? 'bg-blue-100 hover:bg-blue-200' : 'bg-yellow-100 hover:bg-yellow-200'}`}
                                  onClick={() => toggleDia(diaKey)}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <h4 className="font-semibold text-gray-900">{diaLabel}</h4>
                                      {tieneEventos && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-600 text-white">
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                          </svg>
                                          {horasConEventos.length} evento{horasConEventos.length !== 1 ? 's' : ''}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm text-gray-600">
                                        {horas.length} hora{horas.length !== 1 ? 's' : ''}
                                      </span>
                                      <svg
                                        className={`w-4 h-4 text-gray-600 transition-transform ${
                                          expandedDias.has(diaKey) ? 'transform rotate-180' : ''
                                        }`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </div>
                                  </div>
                                  {/* Resumen de eventos cuando está colapsado */}
                                  {tieneEventos && !expandedDias.has(diaKey) && (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      {horasConEventos.map((h) => {
                                        const c = asesorCitas[fecha][h];
                                        return (
                                          <div key={h} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white border border-blue-200 text-xs">
                                            <span className="font-semibold text-blue-700">{h}</span>
                                            <span className="text-gray-600">-</span>
                                            <span className="text-gray-800 truncate max-w-[150px]">
                                              {c.actividad || c.empresa || c.clienteNombre || 'Evento'}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>

                                {/* Horas del día */}
                                {expandedDias.has(diaKey) && (
                                  <div className="divide-y divide-gray-100">
                                    {horas.map((hora) => {
                                      const cita = asesorCitas[fecha][hora];
                                      const estaGuardado = cita.guardado === true;

                                      return (
                                        <div key={hora} className={`p-4 ${estaGuardado ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}>
                                          <div className="grid grid-cols-12 gap-3 items-start">
                                            {/* Hora */}
                                            <div className="col-span-2">
                                              <div className={`text-sm font-medium p-2 rounded ${estaGuardado ? 'bg-blue-100 text-blue-800' : 'bg-gray-50 text-gray-700'}`}>
                                                {hora}
                                                {estaGuardado && (
                                                  <span className="block text-xs mt-1 text-blue-600">
                                                    Evento
                                                  </span>
                                                )}
                                              </div>
                                            </div>

                                            {/* Formulario de cita */}
                                            <div className="col-span-10 space-y-3">
                                              {/* Resumen del evento existente */}
                                              {estaGuardado && (
                                                <div className="bg-blue-100 border border-blue-300 rounded-lg p-3 mb-2">
                                                  <div className="flex items-center gap-2 mb-2">
                                                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                    <span className="font-semibold text-blue-800">Evento programado - {hora}</span>
                                                  </div>
                                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                                                    {cita.actividad && (
                                                      <div><span className="font-medium text-blue-700">Actividad:</span> <span className="text-blue-900">{cita.actividad}</span></div>
                                                    )}
                                                    {cita.empresa && (
                                                      <div><span className="font-medium text-blue-700">Empresa:</span> <span className="text-blue-900">{cita.empresa}</span></div>
                                                    )}
                                                    {cita.clienteNombre && (
                                                      <div><span className="font-medium text-blue-700">Cliente:</span> <span className="text-blue-900">{cita.clienteNombre}</span></div>
                                                    )}
                                                    {cita.ubicacion && (
                                                      <div><span className="font-medium text-blue-700">Ubicación:</span> <span className="text-blue-900">{cita.ubicacion}</span></div>
                                                    )}
                                                  </div>
                                                  {cita.comentarios && (
                                                    <div className="mt-2 text-sm"><span className="font-medium text-blue-700">Comentarios:</span> <span className="text-blue-900">{cita.comentarios}</span></div>
                                                  )}
                                                </div>
                                              )}
                                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                <div>
                                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Actividad
                                                  </label>
                                                  <Input
                                                    value={cita.actividad || ''}
                                                    onChange={(e) => handleCitaChange(asesor.ownerID, fecha, hora, 'actividad', e.target.value)}
                                                    placeholder="Tipo de actividad"
                                                  />
                                                </div>
                                                <div>
                                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Empresa
                                                  </label>
                                                  <select
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    value={cita.empresa || ''}
                                                    onChange={(e) => handleCitaChange(asesor.ownerID, fecha, hora, 'empresa', e.target.value)}
                                                  >
                                                    <option value="">Seleccionar empresa</option>
                                                    {empresasAsignadas.map((emp) => (
                                                      <option key={emp} value={emp}>{emp}</option>
                                                    ))}
                                                  </select>
                                                </div>
                                                <div>
                                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Ubicación
                                                  </label>
                                                  <Input
                                                    value={cita.ubicacion || ''}
                                                    onChange={(e) => handleCitaChange(asesor.ownerID, fecha, hora, 'ubicacion', e.target.value)}
                                                    placeholder="Ubicación"
                                                  />
                                                </div>
                                                <div>
                                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Cliente
                                                  </label>
                                                  <Input
                                                    value={cita.clienteNombre || ''}
                                                    onChange={(e) => handleCitaChange(asesor.ownerID, fecha, hora, 'clienteNombre', e.target.value)}
                                                    placeholder="Nombre del cliente"
                                                  />
                                                </div>
                                                <div>
                                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Nuevo/Renovación
                                                  </label>
                                                  <select
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    value={cita.clienteNuevoRenovacion || ''}
                                                    onChange={(e) => handleCitaChange(asesor.ownerID, fecha, hora, 'clienteNuevoRenovacion', e.target.value)}
                                                  >
                                                    <option value="">Seleccionar</option>
                                                    <option value="Nuevo">Nuevo</option>
                                                    <option value="Renovación">Renovación</option>
                                                  </select>
                                                </div>
      </div>

                                              <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                  Comentarios
                                                </label>
                                                <textarea
                                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                  rows={2}
                                                  value={cita.comentarios || ''}
                                                  onChange={(e) => handleCitaChange(asesor.ownerID, fecha, hora, 'comentarios', e.target.value)}
                                                  placeholder="Comentarios adicionales"
                                                />
                                              </div>

                                              <div className="flex justify-end">
                                                <Button
                                                  size="sm"
                                                  onClick={() => handleGuardarCita(cita, asesor)}
                                                >
                                                  Guardar
                                                </Button>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
          </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default AgendasManagement;
